import React from 'react';
import { connect } from 'react-redux';
import {
    Button,
    Col,
    FormGroup,
    Spinner,
    Row,
} from 'reactstrap';
import {
    isEqual, orderBy, sumBy, isEmpty,
} from 'lodash';
import { v4 as uuid } from 'uuid';
import moment from 'moment';
import { useQueryClient } from '@tanstack/react-query';
import {
    faLock,
    faLockOpen,
    faSave,
    faTruck,
    faTrashAlt,
    faArrowTrendUp,
    faTruckLoading,
    faSync,
} from '@fortawesome/free-solid-svg-icons';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { FormPrompt } from '@s20/components/common/FormPrompt';
import { Link } from '@s20/components/common/Link';
import {
    convertTimezone, getWeekStartDate, resolveJobTimeZone,
} from '@s20/common/util/date';
import { getCounty } from '@s20/common/util/location';
import { clone } from '@s20/common/util/object';
import { httpClient } from '@s20/services/httpClient';
import { getWeekStartsOn } from '@s20/selectors/features/common.selectors';
import ValidatedSelect from '../common/forms/ValidatedSelect';
import {
    AppPageForm,
    FormLabel,
    toasty,
    onReactSelectChanged,
    onFieldChange,
    UncontrolledCollapseUnderlineHeader,
    FormBlocker,
    Input,
    Textarea,
    FlexCenterRow,
    FlexStartRow,
} from '../common/forms/FormElements';
import CommonContext, {
    ApiRoutes, ApplicationPermissions, AppNavPaths, BillingCycles,
} from '../Common';
import { _ContractStatuses } from '../contracts/Contract';
import OrganizationContactForm from '../organization/OrganizationContactForm';
import AddressFormNew from '../address/AddressFormNew';
import { Address } from '../address/Address';
import { BaseFormViewModel } from '../common/ViewModel';
import { OrganizationContact } from '../organization/Organization';
import { Block } from '../common/Block';
import { DatePicker } from '../common/forms/DatePicker';
import { DEFAULT_DATETIME_FORMAT } from '../common/util';
import { EquipmentPickupModal } from '../common/EquipmentPickupModal';
import { getCompanyContactsOptions } from '../contact/Contact';
import { ConfirmDelete, Modal } from '../common/Modal';
import { getDuplicateJobs, showDuplicateJobsModal } from '../job/common/DuplicateJobDetection';
import { ContractProjectProgressBar, progressQueryKey } from '../common/ContractProjectProgressBar';
import { JobScheduler } from './JobScheduler';
import { RUN_APPLIED_FIELDS, applyRunField } from './JobScheduler/runApply';
import { updateResources } from './JobScheduler/common';
import { EquipmentOnSite } from './EquipmentOnSite';
import { ProjectInvoices } from './ProjectInvoices';
import { Project, DuplicateWONote } from './Project';
import LocationSelector from './LocationSelector';
import JobForm from './JobForm';
import 'react-datepicker/dist/react-datepicker.css';

class ProjectForm extends React.Component {
    static contextType = CommonContext;

    constructor(props) {
        super(props);

        const project = new Project();

        this.state = {
            selectedDate: null,
            contract: null,
            contactsOptions: null,
            dispatchLocations: [],
            contractResources: [],
            customerSubcontractors: [],
            slideOpen: false,
            selectedAddressType: null,
            usCounties: [],
            selectedContact: null,
            selectedContactIdProp: null,
            showContactsForm: false,
            allowContactTypeEdit: true,
            contactFormIsReadOnly: false,
            meetingLocationRequired: {},
            jobLocationRequired: {},
            selectedJob: null,
            project: { ...project, ...(props.project || { jobs: [] }) },
            edit: { allowDelete: false },
            jobTypesOptions: null,
            originalProject: clone(new Project()),
            jobsSaving: false,
            showDuplicateWOProjects: false,
            duplicateWOProjects: [],
            originalWO: '',
            billingDates: [],
            billingDate: null,
            resourceKits: [],
            showEquipmentPickupModal: false,
            dispatchPorts: [],
            dispatchPortLoading: false,
            ...new BaseFormViewModel(),
        };

        // Refs
        this.formRef = React.createRef();
        this.addressFormRef = React.createRef();
        this.contactFormRef = React.createRef();

        // Bind any methods if not using arrow functions.
        this.onSubmit = this.onSubmit.bind(this);
        this.onChange = this.onChange.bind(this);
        this.onSelectChanged = this.onSelectChanged.bind(this);
        this.onDispatchOfficeChanged = this.onDispatchOfficeChanged.bind(this);
        this.onOrganizationContactSaved = this.onOrganizationContactSaved.bind(this);
        this.onCloseReopenProject = this.onCloseReopenProject.bind(this);
        this.onUpdateContacts = this.onUpdateContacts.bind(this);
    }

    onSelectChanged = onReactSelectChanged;
    onChange = onFieldChange;

    onDispatchOfficeChanged(selection, meta = {}) {
        this.onSelectChanged(selection, meta);
        this.fetchDispatchPorts(selection?.value ?? null);

        const isQuickRental = Boolean(this.state.contract?.isQuickRentalContract);
        if (isQuickRental && selection) {
            const dispatchAddress = Object.assign(new Address(), {
                address1: selection.address1 ?? '',
                city: selection.city ?? '',
                state: selection.state ?? '',
                zip: selection.zip ?? '',
                countryCode: selection.countryCode ?? '',
                usStateId: selection.uSStateId ?? null,
                countryId: selection.countryId ?? null,
                timeZone: selection.timeZone ?? null,
                usCountyId: selection.uSCountyId ?? null,
                latitude: selection.latitude ?? null,
                longitude: selection.longitude ?? null,
            });
            this.setState((state) => ({
                project: {
                    ...state.project,
                    meetingLocation: dispatchAddress,
                    jobLocation: dispatchAddress,
                },
            }));
        }
    }

    fetchDispatchPorts = async (officeId) => {
        if (!officeId) {
            this.setState({ dispatchPorts: [], dispatchPortLoading: false });
            return;
        }
        this.setState({ dispatchPortLoading: true });
        const ports = await httpClient.js(ApiRoutes.typeAheads.dispatchPorts(officeId));
        const defaultPort = ports?.find((p) => p.isDefault) ?? null;
        this.setState((state) => {
            const portExists = ports?.some((p) => p.value === state.project.dispatchPortId);
            return {
                dispatchPorts: ports ?? [],
                dispatchPortLoading: false,
                project: portExists ? state.project : { ...state.project, dispatchPortId: defaultPort?.value ?? null },
            };
        });
    };

    onClearErrors = () => {
        this.setState({ errors: {} });
    };

    refreshProject = async () => {
        await this.populateState();
    };

    onDeleteConfirmed = async () => {
        const { project } = this.state;
        project.isDeleted = true;

        this.setState({ project });

        await this.onSubmit();
    };

    resetForm = () => {
        const { originalProject } = this.state;

        this.setState({
            isValidated: false,
            errors: {},
            saving: false,
            project: clone(originalProject),
        });
    };

    componentDidMount() {
        const { id, contractId } = this.props.match.params;
        if (id || contractId) {
            this.populateState();
        }
    }

    componentDidUpdate(prevProps) {
        const prevId = prevProps.match.params.projectId || prevProps.match.params.id;
        const nextId = this.props.match.params.projectId || this.props.match.params.id;
        if (nextId && nextId !== prevId) {
            this.populateState();
        }
    }

    async populateState() {
        this.setState({
            loading: true, showDuplicateWOProjects: false,
            duplicateWOProjects: [],
        });

        try {
            // Get parameters from the URL
            const { id, contractId } = this.props.match.params;
            let projectData = null;
            let projectState = { ...this.state.project };

            let contractData = null;

            if (id) {
                const response = await httpClient.js(ApiRoutes.project.byId(id));
                if (response) {
                    projectData = response.project;
                    projectState = projectData;
                    this.setState({ edit: response.edit });
                }
            }

            const contractIdToFetch = contractId || (projectData && projectData.contractId);

            if (contractIdToFetch) {
                if (id && projectData) {
                    contractData = projectData.contract;
                } else {
                    contractData = await httpClient.js(ApiRoutes.contract.byId(contractIdToFetch));
                }

                if (contractData) {
                    projectState = {
                        ...projectState,
                        contractId: contractData.id,
                        contractNumber: contractData.number,
                        contractDescription: contractData.description,
                        contractCustomer: contractData.customerName,
                    };
                }
            }

            const contractResources = contractIdToFetch ? await httpClient.js(ApiRoutes.job.getResources(contractIdToFetch)) : [];
            // Fetch contacts, subcontractors, dispatch locations, and counties if a company exists.
            if (contractData?.companyId) {
                let [contactsData, subcontractors, dispatchLocations, counties, jobTypes, resourceKitList] = await Promise.all([
                    getCompanyContactsOptions(
                        contractData.companyId,
                        false,
                        projectData?.requestedById,
                        projectData?.foremanId,
                        projectData?.projectManagerId,
                    ),
                    httpClient.js(ApiRoutes.typeAheads.subcontractors(contractData.isIntercompany && !contractData.doNotRequireEndCompany ? contractData.endCompanyId : contractData.companyId)),
                    httpClient.js(ApiRoutes.typeAheads.dispatchCompaniesForProject(projectData?.id)),
                    httpClient.js(ApiRoutes.USCounties.all()),
                    httpClient.js(ApiRoutes.typeAheads.jobTypes()),
                    httpClient.js(ApiRoutes.resourceKit.byTenant()),
                ]);

                // Filter dispatch locations based on those allowed by the contract
                dispatchLocations = dispatchLocations.filter((x) => (contractData.dispatchLocations ?? []).includes(x.value));

                // If this is a new project (projectData is null), then assign contract details to the project.
                if (!projectData) {
                    // If only one dispatch location exists, set it automatically
                    if (dispatchLocations.length === 1) {
                        projectState = { ...projectState, dispatchCompanyId: dispatchLocations[0].value };
                    }

                    projectState = {
                        ...projectState,
                        contractId: contractData.id,
                        contractNumber: contractData.number,
                        contractDescription: contractData.description,
                        contractCustomer: contractData.customerName,
                        description: contractData.description,
                        workOrderNumber: id != null ? projectState.workOrderNumber :
                            [contractData.ecmsNumber, contractData.pwProjectNumber]
                                .filter(Boolean)
                                .join(' - '),
                    };
                }
                const hasOnlyJobViewAccess = this.context.checkUserHasPermission(ApplicationPermissions.job_view) &&
                    !this.context.checkUserHasPermission(ApplicationPermissions.job_create) &&
                    !this.context.checkUserHasPermission(ApplicationPermissions.job_edit);

                const resourceKits = (resourceKitList ?? []).map((rk) => ({
                    value: rk.id,
                    label: rk.name,
                    tooltip: rk.description,
                    resources: rk.resources,
                }));
                const dispatchPorts = projectState.dispatchCompanyId
                    ? await httpClient.js(ApiRoutes.typeAheads.dispatchPorts(projectState.dispatchCompanyId))
                    : [];
                const defaultPort = dispatchPorts?.find((p) => p.isDefault) ?? null;
                if (projectState.dispatchPortId != null && !dispatchPorts?.some((p) => p.value === projectState.dispatchPortId)) {
                    projectState.dispatchPortId = defaultPort?.value ?? null;
                }

                this.setState({
                    contractResources,
                    project: projectState,
                    originalProject: clone(projectState),
                    contract: contractData,
                    dispatchLocations: dispatchLocations || [],
                    contactsOptions: contactsData || {},
                    customerSubcontractors: subcontractors || [],
                    usCounties: counties || [],
                    jobTypesOptions: jobTypes || [],
                    originalWO: projectData?.workOrderNumber ?? '',
                    hasOnlyJobViewAccess,
                    resourceKits,
                    dispatchPorts: dispatchPorts ?? [],
                });

                // Check for an openJob query parameter and auto-open the job modal if found.
                const params = new URLSearchParams(this.props.location.search);
                const openJobId = params.get('openJob');

                if (openJobId && projectState.jobs) {
                    const jobToOpen = projectState.jobs.find(
                        (job) => job.id && job.id.toString() === openJobId,
                    );

                    if (jobToOpen) {
                        // Use jobDate if available, otherwise default to today
                        const selectedDate = jobToOpen.jobDate && new Date(jobToOpen.jobDate);
                        this.setState({ selectedJob: jobToOpen, selectedDate, slideOpen: true });
                    }
                }
            }
        } catch (error) {
            console.error('Error fetching data:', error);
            this.setState({ errors: error.message });
        } finally {
            this.setState({ loading: false });
        }
    }

    /**
     * Apply the edited job to the rest of its run. `changedFields` names the run fields the
     * dispatcher changed in this edit (JobForm diffs against its opening snapshot); only those
     * are carried onto the other members, so a day with its own address, Work Order Number or
     * start time is not reverted by an unrelated edit. Without it every field is carried.
     */
    handleRunApplyAllJobs = async (updatedJob, changedFields = new Set(RUN_APPLIED_FIELDS)) => {
        const { project, dispatchLocations } = this.state;
        if (!project?.jobs?.length || !updatedJob) return;

        // Get base dates and IDs
        const runId = updatedJob.parentJobId ?? updatedJob.id;
        const jobsToUpdate = project.jobs.filter((job) =>
            job.id === updatedJob.id
            || (
                (job.id === runId || job.parentJobId === runId)
                && moment(job.startTimeOffset) > moment(updatedJob.startTimeOffset)
            ),
        );

        const [targetHour = 8, targetMinute = 0] = (updatedJob.startTime || '08:00')
            .split(':')
            .map(Number);

        // The update replaces the whole record; the project's job list carries each member's own
        // details, and the edited job's base is what the form holds.
        const members = jobsToUpdate.map((job) => (job.id === updatedJob.id ? { ...job, ...updatedJob } : job));

        // A location picked in the form carries a temporary string id until it is saved.
        const withSavedLocationId = (location) => (typeof location?.id === 'string' ? { ...location, id: null } : location);

        const nextJobs = members.map((job) => {
            const isEditedJob = job.id === updatedJob.id;
            const field = (name) => applyRunField(name, updatedJob, job, changedFields, isEditedJob);
            const carries = (name) => isEditedJob || changedFields.has(name);

            const jobLocation = withSavedLocationId(field('jobLocation'));
            const dispatchOfficeLocationId = field('dispatchOfficeLocationId');
            const timeZone = resolveJobTimeZone({ jobLocation, dispatchOfficeLocationId }, dispatchLocations);

            // This member's start: the typed HH:mm when the dispatcher changed the time, else its
            // own, placed on its own job-site day. `jobDate` is the job-site wall clock (parseZone
            // reads the same day and time off the stored job-zone offset), so the day stays the
            // job's day whatever timezone the viewer is in; the moment carries that wall clock in
            // the browser zone and convertTimezone() re-anchors it into the job's zone before it is
            // sent as the instant.
            const ownStart = moment.parseZone(job.startTimeOffset);
            const [hour, minute] = carries('startTime') ? [targetHour, targetMinute] : [ownStart.hour(), ownStart.minute()];
            const jobDay = job.jobDate ?? ownStart.format('YYYY-MM-DD');
            const newDt = moment(jobDay)
                .hour(hour)
                .minute(minute)
                .second(0)
                .millisecond(0);

            const updatedResources = (updatedJob.resources || []).map((updatedResource) => {
                const existingResource = job.resources?.find((r) =>
                    r.resourceName === updatedResource.resourceName && r.jobResourceTypeId === updatedResource.jobResourceTypeId,
                );

                return {
                    ...updatedResource,
                    id: existingResource?.id || uuid(),
                    jobId: job.id,
                    rentalDropoffQuantity: updatedResource.rentalDropoffQuantity || 0,
                    rentalPickupQuantity: updatedResource.rentalPickupQuantity || 0,
                    rentalDropoffDate: updatedResource.rentalDropoffQuantity > 0 ? newDt.format() : null,
                    rentalPickupDate: updatedResource.rentalPickupQuantity > 0 ? newDt.format() : null,
                    rentalStartDate: updatedResource.rentalDropoffQuantity > 0 ? newDt.format() : null,
                    rentalEndDate: updatedResource.rentalPickupQuantity > 0 ? newDt.format() : null,
                };
            }).filter(Boolean);

            const processedResources = carries('resources')
                ? updateResources(updatedResources, newDt.toDate(), true).map((resource) => ({
                    ...resource,
                    id: typeof resource.id === 'string' ? null : resource.id,
                }))
                : job.resources;

            return {
                ...job,
                foremenId: field('foremenId'),
                requestedById: field('requestedById'),
                projectManagerId: field('projectManagerId'),
                projectName: field('projectName'),
                subcontractorCompanyId: field('subcontractorCompanyId'),
                meetingLocation: withSavedLocationId(field('meetingLocation')),
                jobLocation,
                description: field('description'),
                workOrderNumber: field('workOrderNumber'),
                dispatchOfficeLocationId,
                notes: field('notes'),
                inspectorName: field('inspectorName'),
                jobTypeId: field('jobTypeId'),
                purchaseOrderNumber: field('purchaseOrderNumber'),
                workRequest: field('workRequest'),
                startTime: newDt.format(),
                startTimeOffset: convertTimezone(newDt, timeZone),
                jobDate: newDt.format(),
                weekOfDate: job.weekOfDate ?? getWeekStartDate(newDt.toDate(), this.props.weekStartsOn),
                resources: processedResources,
            };
        });

        if (project.id) {
            await httpClient.put(
                ApiRoutes.job.bulkUpdate(),
                nextJobs,
                httpClient.format.json,
            );

            await this.refreshProject();
            toasty.success('Updated all jobs in this run.');
        }

        this.setState((prevState) => ({
            project: {
                ...prevState.project,
                jobs: this.mergeUpdatedJobs(nextJobs),
            },
        }));
    };

    handleJobCreate = (date) => {
        const { project, contract } = this.state;

        //Prevent adding Jobs when the project is closed
        if (project.isClosed) {
            window.alert('This project is closed. To add jobs, please reopen the project first.');
            return false;
        }

        //Prevent adding Jobs when the contract is inactive
        if (contract && contract.contractStatusId !== _ContractStatuses.Active) {
            window.alert("This contract's status is Inactive or On Hold. Jobs cannot be created against it.");
            return false;
        }

        //Prevent adding Jobs to an invoiced period
        const invoicedDate = project.lastInvoiced
            ? moment(project.lastInvoiced)
            : null;

        if (invoicedDate && invoicedDate >= moment(date)) {
            window.alert('This date has already been invoiced. Please contact the billing team for changes.');
            return false;
        }

        const weekStartDate = getWeekStartDate(date, this.props.weekStartsOn);

        const selectedJob = {
            weekOfDate: new Date(weekStartDate).toISOString(),
            foremenId: project.foremanId,
            requestedById: project.requestedById,
            projectManagerId: project.projectManagerId,
            subcontractorCompanyId: project.subcontractorId,
            meetingLocation: project.meetingLocation,
            jobLocation: project.jobLocation,
            id: null,
        };

        this.setState({ selectedJob, selectedDate: date, slideOpen: true });
    };

    handleJobSelect = (data) => {
        if (data.action === 'refresh') {
            this.refreshProject();
            return;
        }
        if (data.action === 'span-apply') {
            this.handleJobSpanApply(data.payload);
            return;
        }

        if (data.action === 'move') {
            this.handleJobMove({ event: data.event, start: data.start });
            return;
        }
        if (data.action === 'select') {
            const job = this.getJobById(data.event.jobData[0].id);

            this.setState({
                selectedDate: job?.jobDate ? new Date(job.jobDate) : new Date(),
                selectedJob: job,
                slideOpen: true,
            });
        }
    };

    getJobById = (jobId, includeChildJobs = false) => {
        let job = this.state.project.jobs.find((x) => x.id === jobId);

        if (!job) {
            job = this.state.project.jobs.find((x) => x.parentJobId === jobId);
        }

        const jobWithContract = {
            ...job,
            contract: this.state.project.contract,
        };

        if (includeChildJobs) {
            return {
                ...jobWithContract,
                childJobs: this.state.project.jobs.filter((x) => x.parentJobId === jobId && x.id !== job.id),
            };
        }

        return jobWithContract;
    };

    setJobsSaving = (jobsSaving) => this.setState({ jobsSaving });

    handleJobSpanApply = async ({
        sourceJobId, dates, jobs,
    }) => {
        this.setState({ saving: true });
        const sourceJob = this.getJobById(sourceJobId);

        const { project, dispatchLocations } = this.state;

        if (Array.isArray(jobs) && jobs.length) {
            const parent = this.getJobById(sourceJobId);
            const groupingId = parent?.parentJobId || parent?.id || sourceJobId;

            const withLocalIds = jobs.map((j) => {
                const newId = uuid();

                return {
                    ...j,
                    id: newId,
                    tempSessionId: j.tempSessionId || newId,
                    parentJobId: j.parentJobId ?? groupingId,
                    resources: (j.resources || []).map((r) => ({
                        ...r,
                        jobId: 0,
                    })),
                };
            });

            this.setState({
                project: { ...project, jobs: [...project.jobs, ...withLocalIds] },
            });
        } else if (sourceJob) {
            const newChildJobs = (dates || []).map((targetDate) => {
                const newJobId = uuid();

                // The source job's wall clock at the job site, placed on the target day and
                // saved in the job's zone.
                const source = moment.parseZone(sourceJob.startTimeOffset);
                const start = moment(targetDate).hour(source.hour()).minute(source.minute()).second(0).millisecond(0);

                return {
                    ...sourceJob,
                    id: newJobId,
                    tempSessionId: newJobId,
                    parentJobId: sourceJob.parentJobId || sourceJob.id,
                    startTime: start.format(),
                    startTimeOffset: convertTimezone(start, resolveJobTimeZone(sourceJob, dispatchLocations)),
                    jobDate: start.format(),
                    weekOfDate: getWeekStartDate(targetDate, this.props.weekStartsOn),
                    resources: updateResources(sourceJob.resources, targetDate),
                };
            });

            this.setState({
                project: { ...project, jobs: [...project.jobs, ...newChildJobs] },
            });
        } else {
            this.setState({ saving: false });
            return;
        }

        if (project.id) {
            try {
                await httpClient.post(
                    ApiRoutes.project.bulkCreate(),
                    jobs,
                    httpClient.format.json,
                );

                toasty.success('Jobs created successfully');
            } catch (error) {
                console.error('Failed to create jobs:', error);
                if (error?.handledError?.message) {
                    toasty.error('Failed to create jobs.', error.handledError.message);
                } else {
                    toasty.error('Failed to create jobs.');
                }
            } finally {
                await this.refreshProject();
            }
        }

        this.setState({ saving: false });
    };

    /**
    * Moves a single "new" job or a group of jobs to a different date.
    * Updates the jobDate for all jobs in the group, ensuring no overlap with existing jobs.
    */
    handleJobMove = ({ event, start }) => {
        const { project } = this.state;

        // Calculate the time difference for all jobs in the group
        const jobDates = event.jobData.map((job) => new Date(job.jobDate)).sort((a, b) => a - b);
        const jobIds = event.jobData.map((job) => job.id);

        const [earliestDate] = jobDates;
        const timeDifferenceMs = start.getTime() - earliestDate.getTime();
        const dayDifference = Math.round(timeDifferenceMs / (1000 * 60 * 60 * 24));

        // Prepare job updates
        const jobUpdates = event.jobData.map((job) => {
            const newJobDate = new Date(job.jobDate);
            newJobDate.setDate(newJobDate.getDate() + dayDifference);

            job.jobDate = newJobDate.toISOString();
            job.startTime = moment(newJobDate).format(DEFAULT_DATETIME_FORMAT);
            job.weekOfDate = getWeekStartDate(newJobDate, this.props.weekStartsOn);

            return {
                ...job,
                resources: updateResources(job.resources, newJobDate),
            };
        });

        project.jobs = [
            ...project.jobs.filter((job) => !jobIds.includes(job.id)),
            ...jobUpdates,
        ];

        this.setState({
            project,
        });
    };

    handleJobSave = (job) => {
        const { project, selectedDate } = this.state;
        const gid = job.id || uuid();

        const base = {
            ...job,
            id: gid,
            jobDate: moment(selectedDate).format(),
            jobNumber: job.jobNumber || `J${uuid().slice(0, 8)}`,
        };

        const filtered = (project.jobs || []).filter((j) => j.id !== gid);
        const updated = filtered.concat([base]);

        const updatedProject = { ...project, jobs: updated };

        if (!project.id && selectedDate) {
            const jobDay = moment(selectedDate).startOf('day');
            const billingDay = moment(project.billingStartDate).startOf('day');
            if (jobDay.isBefore(billingDay)) {
                updatedProject.billingStartDate = jobDay.format('YYYY-MM-DD');
            }
        }

        this.setState({
            project: updatedProject,
            selectedJob: null,
        });
    };

    onSaveAddress = (address) => {
        const { selectedAddressType, usCounties, project } = this.state;

        if (!selectedAddressType) return;

        const countyData = getCounty(usCounties, address);

        const updatedProject = {
            ...project,
            [selectedAddressType]: { ...address, county: countyData },
        };

        if (address.duplicate) {
            if (selectedAddressType === 'jobLocation') {
                updatedProject.meetingLocation = {
                    ...address, id: updatedProject.meetingLocation?.id ?? uuid(), county: countyData,
                };
            }
            if (selectedAddressType === 'meetingLocation') {
                updatedProject.jobLocation = {
                    ...address, id: updatedProject.jobLocation?.id ?? uuid(), county: countyData,
                };
            }
        }

        this.setState({ project: updatedProject, selectedAddressType: null });
    };
    checkWODuplicate = async (project, originalWO) => {
        this.setState({ showDuplicateWOProjects: false, duplicateWOProjects: [] });
        const workOrderNumber = (project.workOrderNumber ?? '').trim();
        const unchanged = project.id && workOrderNumber === (originalWO ?? '').trim();
        if (!unchanged && workOrderNumber != '' && project.contractId) {
            try {
                const projectId = project.id ?? 0;
                const duplicateWOProjects = await httpClient.js(
                    ApiRoutes.project.isWOExists(
                        project.contractId,
                        encodeURIComponent(project.workOrderNumber),
                        projectId,
                    ),
                );
                const projects = duplicateWOProjects ?? [];
                this.setState({
                    showDuplicateWOProjects: projects.length > 0,
                    duplicateWOProjects: projects,
                });
            } catch (e) {
                console.warn('Duplicate WO validation failed', e);
            }
        }
    };
    handleFieldChange = (value) => {
        const { project, originalWO } = this.state;
        project.workOrderNumber = value;
        this.checkWODuplicate(project, originalWO);
        this.setState((prev) => ({
            project: {
                ...prev.project,
                workOrderNumber: value,
            },
        }));
    };

    onSubmit = async () => {
        const { showDuplicateWOProjects, project, contract } = this.state;

        //Prevent creating a new project when the contract is inactive
        if (!project.id && contract && contract.contractStatusId !== _ContractStatuses.Active) {
            toasty.error("This contract's status is Inactive or On Hold. Projects cannot be created against it.");
            return false;
        }

        if (!this.validateProject()) return false;

        if (showDuplicateWOProjects && project.workOrderNumber && !project.id) {
            this.showDuplicateWOModal();
            return false;
        } else {
            await this.createProject();
        }
    };

    createProject = async () => {
        const { project, selectedDate } = this.state;

        this.setState({ saving: true });

        try {
            const projectToSubmit = { ...project };

            projectToSubmit.meetingLocation = typeof projectToSubmit.meetingLocation?.id === 'string'
                ? { ...projectToSubmit.meetingLocation, id: null }
                : projectToSubmit.meetingLocation;

            projectToSubmit.jobLocation = typeof projectToSubmit.jobLocation?.id === 'string'
                ? { ...projectToSubmit.jobLocation, id: null }
                : projectToSubmit.jobLocation;

            projectToSubmit.jobs = (projectToSubmit.jobs || []).map((job) => ({
                ...job,
                resources: (job.resources || []).map((r) => ({
                    ...r,
                    id: typeof r.id === 'string' ? null : r.id,
                    jobId: typeof job.id === 'string' ? null : job.id,
                    rentalDropoffDate: r.rentalDropoffQuantity > 0
                        ? moment(job.jobDate || selectedDate).format()
                        : null,
                    rentalPickupDate: r.rentalPickupQuantity > 0
                        ? moment(job.jobDate || selectedDate).format()
                        : null,
                })),
            }));

            // Make sure the jobs that don't have parent ids are processed first
            const pJobs = orderBy(projectToSubmit.jobs, (job) => job.parentJobId || '');

            // Group jobs so that child jobs are contained within the parent
            projectToSubmit.jobs = pJobs.reduce((results, job) => {
                if (!job.parentJobId) {
                    results[job.id] = {
                        ...job,
                        childJobs: [],
                    };
                } else {
                    const parent = results[job.parentJobId];
                    if (parent) {
                        parent.childJobs.push({
                            ...job,
                            id: typeof job.id === 'string' ? null : job.id,
                        });
                        results[job.parentJobId] = parent;
                    } else {
                        console.warn(`Parent not found for job ${job.id} with parentJobId ${job.parentJobId}`);
                        results[job.id] = {
                            ...job,
                            parentJobId: null,
                            childJobs: [],
                        };
                    }
                }
                return results;
            }, {});

            // Null the parent job Ids
            projectToSubmit.jobs = Object.values(projectToSubmit.jobs).map((job) => ({
                // null out any draft parent’s own id
                ...job,
                id: typeof job.id === 'string' ? null : job.id,

                // now fix up the children
                childJobs: (job.childJobs || []).map((child) => ({
                    ...child,
                    // null out any draft parentJobId on each child
                    parentJobId: typeof child.parentJobId === 'string'
                        ? null
                        : child.parentJobId,
                })),
            }));

            if (!project.id && project.jobs?.length > 0) {
                const duplicateJobRequests = project.jobs
                    .filter((job) => job.jobLocation && job.jobDate)
                    .map((job) => ({
                        jobDate: moment(job.jobDate).toISOString(),
                        jobLocation: job.jobLocation,
                        excludeJobId: null,
                    }));

                if (duplicateJobRequests.length > 0) {
                    const duplicateJobsList = await getDuplicateJobs({ duplicateJobRequests });

                    if (duplicateJobsList.length > 0) {
                        this.setState({ saving: false });
                        const proceed = await showDuplicateJobsModal(duplicateJobsList);
                        if (!proceed) return;
                        this.setState({ saving: true });
                    }
                }
            }

            await this.saveProjectToServer(projectToSubmit, pJobs);
        } catch (error) {
            console.error('Error saving project:', error);
            this.resetForm();
            toasty.error(error.message);
        } finally {
            this.setState({
                saving: false,
            });
        }
    };

    saveProjectToServer = async (projectToSubmit, pJobs) => {
        const { project } = this.state;

        this.setState({ saving: true });

        const projectWithoutJobs = { ...projectToSubmit };
        delete projectWithoutJobs.jobs;

        const originalProjectWithoutJobs = { ...this.state.originalProject };
        delete originalProjectWithoutJobs.jobs;

        const isProjectModified = !isEqual(projectWithoutJobs, originalProjectWithoutJobs);

        let projectId = project.id;

        if (!project.id || isProjectModified) {
            const apiRoute = project.id
                ? ApiRoutes.project.update(project.id)
                : ApiRoutes.project.create();

            const response = project.id
                ? await httpClient.put(apiRoute, projectWithoutJobs, httpClient.format.none)
                : await httpClient.post(apiRoute, projectToSubmit, httpClient.format.none);

            if (!response.ok) throw new Error('Failed to save project');

            const savedProjectData = await response.json();
            projectId = project.id ? project.id : savedProjectData?.data;
            projectToSubmit = { ...projectToSubmit, id: projectId };
        }

        // Check if project was deleted and redirect to contract page
        if (project.isDeleted) {
            toasty.success('Project deleted successfully!');
            this.context.history.push(`/contract/${project.contractId}`);
            return;
        }

        toasty.success(`Project ${project.id ? 'updated' : 'created'} successfully!`);

        const updatedProject = { ...projectToSubmit, jobs: pJobs };

        this.setState({
            project: updatedProject,
            originalProject: clone(updatedProject),
        }, () => {
            this.resetForm();

            if (!project.id) {
                this.context.history.push(`/project/${projectId}`);
            }
        });
    };

    onCloseReopenProject = async () => {
        const { saving, project } = this.state;

        this.setState({ saving: false });
        const actionName = project.isClosed ? 'Reopen' : 'Close';
        const confirmed = await Modal.ask({
            title: `${actionName} Project`,
            body: () => `Are you sure you want to ${actionName} this project?`,
            SaveButtonProps: { label: `${actionName} Project`, color: 'danger' },
        });
        if (!confirmed) return;

        if (saving) {
            return false;
        }

        if (!project.isClosed && sumBy(project.resources, 'actualQuantity') > 0) {
            toasty.error('Unable to close this project as it still has equipment on site');
            return;
        }

        this.setState({ saving: true });

        try {
            const projectToUpdate = { ...project };

            //Check Unsaved changed
            const projectWithoutJobs = { ...projectToUpdate };
            delete projectWithoutJobs.jobs;

            const originalProjectWithoutJobs = { ...this.state.originalProject };
            delete originalProjectWithoutJobs.jobs;

            // Closure validation warning
            if (!projectToUpdate?.isClosed) {
                try {
                    const validation = await httpClient.js(ApiRoutes.project.closureValidation(project.id));
                    if (validation) {
                        const hasWarnings = (validation.incompleteItems?.length > 0)
                            || (validation.unusedEquipment?.length > 0);

                        if (hasWarnings) {
                            this.setState({ saving: false });
                            const warningBody = (
                                <div>
                                    {validation.incompleteItems?.length > 0 && (
                                        <>
                                            <p className="font-weight-bold">Incomplete Estimate Items:</p>
                                            <ul>
                                                {validation.incompleteItems.map((item) => (
                                                    <li key={item.contractChargeTypeId}>
                                                        {item.chargeTypeName}
                                                        {' - '}
                                                        {item.usedQuantity}
                                                        {' / '}
                                                        {item.estimateQuantity}
                                                        {' '}
                                                        {item.unitName}
                                                        {' ('}
                                                        {item.percentage}
                                                        %)
                                                    </li>
                                                ))}
                                            </ul>
                                        </>
                                    )}
                                    {validation.unusedEquipment?.length > 0 && (
                                        <>
                                            <p className="font-weight-bold">Unused Equipment:</p>
                                            <ul>
                                                {validation.unusedEquipment.map((eq) => (
                                                    <li key={eq.contractChargeTypeId}>{eq.chargeTypeName}</li>
                                                ))}
                                            </ul>
                                        </>
                                    )}
                                </div>
                            );

                            const proceed = await Modal.ask({
                                title: 'Project Closure Warning',
                                body: () => warningBody,
                                SaveButtonProps: {
                                    label: 'Close Anyway',
                                    color: 'warning',
                                },
                            });

                            if (!proceed) return;
                            this.setState({ saving: true });
                        }
                    }
                } catch (err) {
                    console.error('Error fetching closure validation:', err);
                    toasty.warning('Could not check project completion status. Proceeding with closure.');
                }
            }

            const apiRoute = ApiRoutes.project.closeReopenProject(project.id, !projectToUpdate?.isClosed);
            const response = await httpClient.put(apiRoute, project.id, httpClient.format.none);
            if (response.ok) {
                const serviceResponse = await response.json();
                if (serviceResponse.result === 'SUCCESS') {
                    const updatedProject = { ...projectToUpdate, isClosed: !projectToUpdate?.isClosed, closedOn: new Date() };
                    this.setState({
                        project: updatedProject,
                        originalProject: clone(updatedProject),
                    });
                    toasty.success(
                        `Project ${projectToUpdate?.isClosed ? 'opened' : 'closed'} successfully!`,
                    );
                } else {
                    toasty.error(`Failed to ${projectToUpdate?.isClosed ? 'reopen' : 'close'} project`, serviceResponse.message);
                }
            }
        } catch (error) {
            console.error('Error updating project:', error);
            this.resetForm();
            this.setState((prevState) => ({ formState: { ...prevState.formState, errors: error?.message || 'An error occurred while updating the project.' } }));
        } finally {
            this.setState({ saving: false });
        }
    };

    validateProject = () => {
        let valid = true;
        const errors = {};
        const requiredStyle = {
            border: '1px solid',
            borderRadius: '0.25rem',
            borderColor: '#ed1c24',
        };
        const { project, contract } = this.state;

        // Check meeting location
        if (!project.meetingLocation) {
            valid = false;
            errors.meetingLocation = 'Meeting location is required.';
            this.setState({ meetingLocationRequired: requiredStyle });
        } else {
            this.setState({ meetingLocationRequired: {} });
        }

        // Check job location
        if (!project.jobLocation) {
            valid = false;
            errors.jobLocation = 'Job location is required.';
            this.setState({ jobLocationRequired: requiredStyle });
        } else {
            this.setState({ jobLocationRequired: {} });
        }

        // Check foreman
        if (!project.requestedById) {
            valid = false;
            errors.requestedBy = 'A requested by selection is required.';
        }

        // Check foreman
        if (!project.foremanId) {
            valid = false;
            errors.foreman = 'A foreman selection is required.';
        }

        // Check Dispatching
        if (!project.dispatchCompanyId) {
            valid = false;
            errors.dispatchCompany = 'A dispatch selection is required.';
        }

        // Check project name if required by the contract/tenant setting
        if ((contract ?? {}).requireProjectInformation && !project.projectName) {
            valid = false;
            errors.projectName = 'Project Name is required.';
        }

        // Check project name if required by the contract/tenant setting
        if ((contract ?? {}).requireProjectInformation && !project.projectManagerId) {
            valid = false;
            errors.projectManager = 'Project Manager is required.';
        }

        // Check subcontractor if subcontractorsApply is true
        if ((contract ?? {}).subcontractorsApply && !project.subcontractorId) {
            valid = false;
            errors.subcontractorId = 'A subcontractor selection is required.';
        }

        // Optionally show a general message if not valid
        if (!valid) {
            toasty.error('Please fill in all the required details.');
        }

        this.setState({ isValidated: valid, errors, saving: valid });

        return valid;
    };

    onAddContact = (prop) => {
        const { contract } = this.state;
        const { setFormOpened } = this.context;

        if (!contract) return;

        if (setFormOpened) setFormOpened(true);

        if (this.contactFormRef.current) {
            this.contactFormRef.current.resetForm();
        }
        const newContact = new OrganizationContact();
        newContact.id = null;
        newContact.companyId = contract.companyId;

        this.setState({
            selectedContact: newContact,
            selectedContactIdProp: prop,
            showContactsForm: true,
            allowContactTypeEdit: true,
        });
    };

    onEditContact = async (prop, readOnly = false) => {
        const { project } = this.state;
        const { setFormOpened } = this.context;

        if (!project[prop]) return;

        if (setFormOpened) setFormOpened(true);

        try {
            const selectedContactData = await httpClient.js(ApiRoutes.locationContact.byId(project[prop]));
            if (this.contactFormRef.current) {
                this.contactFormRef.current.resetForm();
            }
            this.setState({
                contactFormIsReadOnly: readOnly,
                selectedContact: selectedContactData,
                selectedContactIdProp: prop,
                showContactsForm: true,
                allowContactTypeEdit: false,
            });
        } catch (error) {
            console.error('Error fetching contact:', error);
        }
    };

    onOrganizationContactSaved = async () => {
        const { selectedContact, selectedContactIdProp, contract } = this.state;

        if (!selectedContactIdProp || !selectedContact) return;

        this.setState({ contactFormIsReadOnly: true });
        const isNew = !selectedContact.id;

        try {
            const result = await httpClient.andGetResponse(
                isNew ? httpClient.types.post : httpClient.types.put,
                isNew ? ApiRoutes.locationContact.create() : ApiRoutes.locationContact.update(selectedContact.id),
                selectedContact,
            );
            if (result) {
                toasty.success('Contact saved successfully.');

                const updatedProject = { ...this.state.project, [selectedContactIdProp]: result.id ?? result };
                this.setState({ project: updatedProject });

                const companyContacts = await getCompanyContactsOptions(contract.companyId);
                this.setState({ contactsOptions: companyContacts });
                this.setState({ showContactsForm: false });
            }
        } catch (error) {
            console.error('Error saving contact:', error);
        } finally {
            this.setState({ contactFormIsReadOnly: false });
        }
    };

    onUpdateContacts = (contacts) => {
        this.setState({ contactsOptions: contacts });
    };

    getDispatchPath(dispatchOfficeLocationId) {
        const { user } = this.context;
        let path = `${AppNavPaths.Dispatch}`;

        if (
            user.isAdmin ||
            (
                Array.isArray(user.expandedDispatchCompanies) &&
                user.expandedDispatchCompanies.includes(dispatchOfficeLocationId)
            )
        ) {
            path += `?dispatchOfficeId=${dispatchOfficeLocationId}`;
        }

        return path;
    }

    mergeUpdatedJobs = (updatedJobs) => {
        const { project } = this.state;

        // Merge updates in with the rest of the jobs in the project
        const stateJobs = project.jobs.reduce((results, job) => {
            const update = updatedJobs.find((j) => j.id === job.id);

            if (!update) {
                return [...results, job];
            }

            if (update.isDeleted) {
                return results;
            }

            return [
                ...results,
                { ...job, ...update },
            ];
        }, []);

        return stateJobs;
    };

    handleJobDelete = async (jobToDelete) => {
        const { project } = this.state;

        // Get all jobs in the run sorted by date
        const runId = jobToDelete.parentJobId || jobToDelete.id;
        const jobsInRun = project.jobs
            .filter((j) => j.parentJobId === runId || j.id === runId)
            .sort((a, b) => moment(a.jobDate).valueOf() - moment(b.jobDate).valueOf());

        // Remove the job to delete
        let updatedJobs = jobsInRun.map((j) => ({
            ...j,
            isDeleted: j.id === jobToDelete.id,
            parentJobId: j.id === jobToDelete.id ? null : j.parentJobId,
        }));

        // Handle remaining jobs in the run
        const remainingJobs = jobsInRun.filter((j) => j.id !== jobToDelete.id && moment(j.jobDate) > moment(jobToDelete.jobDate));

        if (remainingJobs.length > 0) {
            const [newParentJob] = remainingJobs;

            updatedJobs = updatedJobs.map((job) => {
                const isInRemainingJobs = remainingJobs.find((r) => r.id === job.id);
                if (!isInRemainingJobs) return job;

                return {
                    ...job,
                    parentJobId: job.id === newParentJob.id ? null : newParentJob.id,
                };
            });
        }

        // Send bulk update
        if (typeof jobToDelete.id !== 'string' && updatedJobs.length) {
            await httpClient.put(
                ApiRoutes.job.bulkMove(),
                updatedJobs.map((j) => ({
                    ...j,
                    startTime: j.startTimeOffset,
                })),
                httpClient.format.json,
            );

            await this.refreshProject();
        }

        this.setState((prevState) => ({
            project: {
                ...prevState.project,
                jobs: this.mergeUpdatedJobs(updatedJobs),
            },
        }));
    };

    onSyncEstimate = async () => {
        const { project } = this.state;
        try {
            await httpClient.andGetResponse(
                httpClient.types.post,
                ApiRoutes.project.syncEstimate(project.contractId),
                null,
                'Failed to sync estimate quantities',
            );
            toasty.success('Estimate quantities synced successfully');

            this.props.queryClient.invalidateQueries({ queryKey: progressQueryKey(null, project.id) });
        } catch (error) {
            console.error('Error syncing estimate:', error);
        }
    };

    getActions = () => {
        const { project, edit, contract } = this.state;

        const canInvoice = this.context.checkUserHasPermission(ApplicationPermissions.invoice_create);
        const canToggleClosed = this.context.checkUserHasPermission(
            project.isClosed ? ApplicationPermissions.job_edit : ApplicationPermissions.project_close,
        );
        const hasEquipmentOnSite = project.resources.some((rs) => rs.actualQuantity > 0);

        const actions = [];

        if (project?.id) {
            if (canToggleClosed) {
                actions.push({
                    key: 'close',
                    onClick: this.onCloseReopenProject,
                    icon: project.isClosed ? faLockOpen : faLock,
                    label: project.isClosed ? 'Reopen Project' : 'Close Project',
                });
            }

            if (contract?.createdFromId && !project.isClosed) {
                actions.push({
                    key: 'syncEstimate',
                    onClick: this.onSyncEstimate,
                    icon: faSync,
                    label: 'Sync Estimate',
                });
            }

            if (!isEmpty(project.jobs) && hasEquipmentOnSite && !project.isClosed) {
                actions.push({
                    key: 'pickUpSite',
                    onClick: this.openEquipmentPickupModal,
                    icon: faTruckLoading,
                    label: 'Pick Up Site Equipment',
                });
            }

            if (!isEmpty(project.jobs) && canInvoice) {
                actions.push({
                    key: 'invoice',
                    onClick: this.toggleInvoiceModal,
                    icon: faArrowTrendUp,
                    label: 'Invoice Project',
                });
            }

            if (edit.allowDelete) {
                actions.push({
                    key: 'delete',
                    onClick: () => this.showDeleteModal(),
                    icon: faTrashAlt,
                    label: 'Delete Project',
                    className: 'text-danger',
                });
            }
        }
        if (actions.length > 0) {
            return actions;
        }

        return null;
    };

    openEquipmentPickupModal = () => {
        const { project } = this.state;
        const hasEquipmentOnSite = (project.resources ?? []).some((rs) => rs.actualQuantity > 0);
        if (!hasEquipmentOnSite) {
            toasty.error('There is no equipment currently on site.');
            return;
        }
        this.setState({ showEquipmentPickupModal: true });
    };


    toggleInvoiceModal = () => {
        const { project, contract } = this.state;

        const dates = [];
        const lastEndDate = project.closedOn ? moment(project.closedOn) : moment();

        if (contract.billingCycleId == BillingCycles.Daily) {
            let billingDate = project?.lastInvoiced ?
                moment(project?.lastInvoiced).add(2, 'day').toDate() :
                moment(project.billingStartDate).add(1, 'day').toDate();

            while (moment(billingDate).startOf('day') <= lastEndDate.startOf('day')) {
                dates.push(billingDate);
                billingDate = moment(billingDate).add(1, 'day').toDate();
            }
        } else {
            let billingDate = contract.billingCycleId == BillingCycles.Weekly ?
                this.getNextWeeklyBillingDate(project.billingStartDate) :
                this.getNextMonthlyBillingDate(project.billingStartDate);

            while (moment(billingDate) < lastEndDate) {
                dates.push(billingDate);
                billingDate = contract.billingCycleId == BillingCycles.Weekly ?
                    this.getNextWeeklyBillingDate(billingDate) :
                    this.getNextMonthlyBillingDate(billingDate);
            }

            //Add the project close day, if not billed
            if (project.closedOn && (!project?.lastInvoiced || moment(project.closedOn) > moment(project?.lastInvoiced))) {
                dates.push(moment(project.closedOn).add(1, 'day').toDate());
            }

            if (dates.length == 0 || (project?.lastInvoiced && !dates.find((d) => moment(d) > moment(project?.lastInvoiced).add(1, 'day')))) {
                toasty.warning(project.lastInvoiced != null ? 'This project has already been fully billed.' : 'There is nothing to be billed yet.');
                return;
            }
        }

        const minDate = project.lastInvoiced ? moment(project.lastInvoiced).add(2, 'day').toDate() : moment(project.billingStartDate).add(1, 'day').toDate();

        Modal.show({
            title: 'Invoice Project',
            onSubmit: (values) => this.onGenerateInvoice(values),
            SaveButtonProps: {
                label: 'Generate',
                savingLabel: 'Generating',
            },
            body: () => (
                <DatePicker
                    id="billingDate"
                    label="Billing Period"
                    name="billingDate"
                    className="form-control"
                    minDate={minDate}
                    maxDate={project.closedOn ? moment(project.closedOn).add(1, 'day').toDate() : new Date()}
                    highlightDates={dates}
                    filterDate={(date) => {
                        const isHighlighted = dates.some(
                            (d) => moment(d).isSame(date, 'day'),
                        );
                        return isHighlighted;
                    }}
                    required
                />
            ),
        });
    };

    getNextWeeklyBillingDate = (lastBillingDate) => {
        const { contract } = this.state;
        if (contract.weeklyBillingCycleEndDay != null) {
            const nextMatch = moment(lastBillingDate).day(contract.weeklyBillingCycleEndDay + 1);
            // If the calculated day is same or before the lastBillingDate, move to next week
            while (nextMatch.isSameOrBefore(lastBillingDate, 'day') ||
                (!contract.companyBillFirstPartialPeriod && nextMatch.isBefore(moment(lastBillingDate).add(7, 'days'), 'day'))) {
                nextMatch.add(1, 'week');
            }
            return nextMatch.toDate();
        }

        // Default: add 7 days
        return moment(lastBillingDate).add(7, 'days').toDate();
    };

    getNextMonthlyBillingDate = (lastBillingDate) => {
        const { contract } = this.state;
        const { tenantSettings } = this.context.tenant;
        if (contract.monthlyBillingCycleEndDay != null) {
            const nextMatch = moment(lastBillingDate)
                .startOf('month')
                .date(contract.monthlyBillingCycleEndDay + 1);

            // If the computed date is before or same as lastBillingDate, move to next month
            while (nextMatch.isSameOrBefore(lastBillingDate, 'day') ||
                (!contract.companyBillFirstPartialPeriod && nextMatch.isBefore(moment(lastBillingDate).add(30, 'days'), 'day'))) {
                nextMatch.add(1, 'month');
            }
            return nextMatch.toDate();
        }

        // Default fallback: add 30 days or the 1st of the month if Month End setting enabled
        return tenantSettings.enableMonthEndRentalInvoice ?
            moment(lastBillingDate).add(1, 'month').startOf('month').toDate() :
            moment(lastBillingDate).add(30, 'days').toDate();
    };

    onGenerateInvoice = async (values) => {
        try {
            const { project } = this.state;
            const { billingDate } = values;

            const apiRoute = ApiRoutes.project.invoice(project.id);
            const response = await httpClient.post(apiRoute, moment(billingDate).add(-1, 'day'), httpClient.format.none);

            const serviceResponse = await response.json();
            if (!response.ok) throw new Error(serviceResponse?.message || 'Failed to invoice project');

            toasty.success(
                `Invoice ${serviceResponse} created successfully!`,
            );

            const updatedProject = { ...project, lastInvoiced: moment(billingDate).add(-1, 'day').hours(23).minutes(59).toDate() };
            this.setState({ project: updatedProject });
        } catch (error) {
            toasty.error(
                error?.message || 'An error occurred while billing the project.',
            );
        }
    };

    showDeleteModal = () => {
        ConfirmDelete.show({
            title: 'Delete Project',
            onSubmit: () => this.onDeleteConfirmed(),
            body: () => 'Are you sure you want to delete this project?',
        });
    };

    showDuplicateWOModal = () => {
        const { project, duplicateWOProjects } = this.state;

        Modal.show({
            title: 'Duplicate Work Order Number',
            onSubmit: () => this.createProject(),
            SaveButtonProps: {
                label: 'Continue',
            },
            body: () => (
                <DuplicateWONote
                    project={project}
                    duplicateWOProjects={duplicateWOProjects}
                />
            ),
        });
    };

    render() {
        const {
            selectedDate,
            contract,
            contractResources,
            contactsOptions,
            dispatchLocations,
            customerSubcontractors,
            slideOpen,
            project,
            meetingLocationRequired,
            jobLocationRequired,
            selectedJob,
            usCounties,
            showContactsForm,
            contactFormIsReadOnly,
            allowContactTypeEdit,
            selectedContact,
            originalProject,
            saving,
            isValidated,
            errors,
            loading,
            jobTypesOptions,
            jobsSaving,
            showDuplicateWOProjects,
            duplicateWOProjects,
            hasOnlyJobViewAccess,
            resourceKits,
            showEquipmentPickupModal,
            dispatchPorts,
            dispatchPortLoading,
        } = this.state;

        const { tenantSettings } = this.context.tenant;
        const existing = !!project.id;

        const projectWithoutJobs = { ...project };
        delete projectWithoutJobs.jobs;

        const originalProjectWithoutJobs = { ...originalProject };
        delete originalProjectWithoutJobs.jobs;

        const showConfirmationPrompt =
            !saving &&
            (!isEqual(originalProjectWithoutJobs, projectWithoutJobs));

        const dispatchPath = this.getDispatchPath(project?.dispatchCompanyId);

        const selectedDispatchLocation = (dispatchLocations ?? []).find(
            (x) => x.value === project.dispatchCompanyId,
        );
        return (
            <>
                <FormPrompt
                    when={showConfirmationPrompt}
                    message="You have unsaved changes, are you sure you want to leave?"
                />
                <AppPageForm
                    formShown={this.context.formIsOpen}
                    backUrl={`${AppNavPaths.Contract}/${project.contractId}`}
                    backLabel="Go to Contract"
                    formId="projectForm"
                    formHeadingIcon={project?.isClosed ? faLock : faTruck}
                    formHeading={!existing
                        ? 'New Project'
                        : `Edit Project: ${project.contractNumber}${project.contractSequence ? `-${project.contractSequence}` : ''}`
                    }
                    formName="projectForm"
                    formRef={this.formRef}
                    onSubmit={this.onSubmit}
                    setIsValidated={(value) => {
                        this.setState({ isValidated: value });
                    }}
                    isValidated={isValidated}
                    saving={saving}
                    errors={errors}
                    loading={loading}
                    onClearErrors={this.onClearErrors}
                    actions={this.getActions()}
                    viewOnly={hasOnlyJobViewAccess}
                >

                    <Row className="mt-3 align-items-start mb-4">
                        <Col xs={6}>
                            <Row>
                                <Col xs={contract?.subcontractorsApply ? 6 : 12}>
                                    <ValidatedSelect
                                        id="project.dispatchCompanyId"
                                        name="project.dispatchCompanyId"
                                        formGroupClassName="mb-4"
                                        label="Dispatching"
                                        options={dispatchLocations}
                                        value={
                                            selectedDispatchLocation ?? ''
                                        }
                                        onChange={this.onDispatchOfficeChanged}
                                        required
                                        error="A dispatch selection is required."
                                        className="fullWidth"
                                    />
                                    {!!project.dispatchCompanyId && !!dispatchPorts.length && (
                                        <ValidatedSelect
                                            id="dispatchPortId"
                                            name="project.dispatchPortId"
                                            formGroupClassName="mb-4"
                                            label="Dispatch Port"
                                            placeholder="Use office location"
                                            options={dispatchPorts}
                                            value={dispatchPorts.find((o) => o.value === project.dispatchPortId) ?? null}
                                            onChange={this.onSelectChanged}
                                            isLoading={dispatchPortLoading}
                                            disabled={dispatchPortLoading}
                                            className="fullWidth"
                                        />
                                    )}
                                </Col>

                                {contract?.subcontractorsApply && (
                                    <Col xs={6}>
                                        <ValidatedSelect
                                            id="subcontractorId"
                                            name="project.subcontractorId"
                                            label="Subcontractor"
                                            formGroupClassName="mb-4"
                                            options={customerSubcontractors || []}
                                            value={
                                                (customerSubcontractors ?? []).find(
                                                    (x) => x.value === project.subcontractorId,
                                                ) ?? ''
                                            }
                                            onChange={this.onSelectChanged}
                                            required={contract?.subcontractorsApply}
                                            error="A subcontractor selection is required."
                                            className="fullWidth"
                                        />
                                    </Col>
                                )}
                            </Row>

                            <ValidatedSelect
                                id="requestedBy"
                                name="project.requestedById"
                                label="Requested By"
                                options={contactsOptions?.companyContactsOptions}
                                value={
                                    (contactsOptions?.companyContactsOptions ?? []).find(
                                        (x) => x.value === project.requestedById,
                                    ) ?? ''
                                }
                                onChange={this.onSelectChanged}
                                required
                                error="A requested by selection is required."
                                showAddButton
                                onAddButtonClick={() => this.onAddContact('requestedById')}
                                showEditButton={!!project?.requestedById}
                                onEditButtonClick={() => this.onEditContact('requestedById')}
                                showViewButton={!!project?.requestedById}
                                onViewButtonClick={() => this.onEditContact('requestedById', true)}
                            />

                            <ValidatedSelect
                                id="foremen"
                                name="project.foremanId"
                                label="Foreman"
                                options={contactsOptions?.companyContactsOptions}
                                value={
                                    (contactsOptions?.companyContactsOptions ?? []).find(
                                        (x) => x.value === project.foremanId,
                                    ) ?? ''
                                }
                                onChange={this.onSelectChanged}
                                required
                                error="A foreman selection is required."
                                showAddButton
                                onAddButtonClick={() => this.onAddContact('foremanId')}
                                showEditButton={!!project?.foremanId}
                                onEditButtonClick={() => this.onEditContact('foremanId')}
                                showViewButton={!!project?.foremanId}
                                onViewButtonClick={() => this.onEditContact('foremanId', true)}
                            />
                        </Col>

                        <Col xs={6}>
                            <Block
                                className="locationDetails p-2"
                            >
                                <FormGroup>
                                    <FormLabel
                                        htmlFor="contractNumber"
                                        label="Contract Number"
                                        className="bottom-border"
                                    />
                                    <FlexStartRow>
                                        <span
                                            id="contractNumber"
                                            name="contractNumber"
                                        >
                                            {(contract ?? {}).number}
                                        </span>
                                    </FlexStartRow>
                                </FormGroup>

                                <FormGroup>
                                    <FormLabel
                                        htmlFor="contractDescription"
                                        label="Description"
                                        className="bottom-border"
                                    />
                                    <FlexStartRow>
                                        <span
                                            id="contractDescription"
                                            name="contractDescription"
                                        >
                                            {(contract ?? {}).description}
                                        </span>
                                    </FlexStartRow>
                                </FormGroup>

                                <FormGroup>
                                    <FormLabel
                                        htmlFor="customerName"
                                        label="Customer Name"
                                        className="bottom-border"
                                    />
                                    <FlexStartRow>
                                        <span
                                            id="customerName"
                                            name="customerName"
                                        >
                                            {(contract ?? {}).customerName}
                                        </span>
                                    </FlexStartRow>
                                </FormGroup>

                                <FormGroup>
                                    <FormLabel
                                        htmlFor="lastInvoiced"
                                        label="Last Invoiced Date"
                                        className="bottom-border"
                                    />
                                    <FlexStartRow>
                                        <span
                                            id="lastInvoiced"
                                            name="lastInvoiced"
                                        >
                                            {project?.lastInvoiced
                                                ? moment(project.lastInvoiced).format('MM/DD/YYYY')
                                                : '--'}
                                        </span>
                                    </FlexStartRow>
                                </FormGroup>

                                {(contract ?? {}).operationsCenterName && (
                                    <FormGroup>
                                        <FormLabel
                                            htmlFor="operationsCenterName"
                                            label="Operations Center"
                                            className="bottom-border"
                                        />

                                        <FlexStartRow>
                                            <span
                                                id="operationsCenterName"
                                                name="operationsCenterName"
                                            >
                                                {(contract ?? {}).operationsCenterName}
                                            </span>
                                        </FlexStartRow>
                                    </FormGroup>
                                )}

                                {((contract ?? {}).isUnionWork === true || (contract ?? {}).companyUsesContractSchedule === true) && (
                                    <FormGroup>
                                        <FormLabel
                                            htmlFor="schedule"
                                            label="Schedule"
                                            className="bottom-border"
                                        />

                                        <FlexStartRow>
                                            <span
                                                id="schedule"
                                                name="schedule"
                                            >
                                                {(contract ?? {}).contractScheduleTypeName}
                                            </span>
                                        </FlexStartRow>
                                    </FormGroup>
                                )}

                            </Block>

                            {Boolean(existing && contract?.createdFromId) && (
                                <ContractProjectProgressBar
                                    label="Project Progress"
                                    className="mt-5"
                                    projectId={project.id}
                                    canViewDetails={this.context.checkUserHasPermission(
                                        ApplicationPermissions.job_view,
                                    )}
                                />
                            )}
                        </Col>
                    </Row>

                    <Row>
                        <Col xs={6}>
                            {contract?.workOrderTypeId !== 3 && (
                                <>
                                    <Input
                                        name="project.workOrderNumber"
                                        label="Work Order #"
                                        maxLength={44}
                                        value={project.workOrderNumber ?? ''}
                                        onChange={(e) => this.handleFieldChange(e.target.value)}
                                        formGroupClassName={showDuplicateWOProjects && project.workOrderNumber ? 'mb-0' : 'mb-4'}
                                    />

                                    {showDuplicateWOProjects && project.workOrderNumber && (
                                        <DuplicateWONote
                                            project={project}
                                            duplicateWOProjects={duplicateWOProjects}
                                        />
                                    )}
                                </>
                            )}

                            {contract?.companyRequiresWorkRequest && (
                                <Input
                                    name="project.workRequest"
                                    label="Work Request"
                                    maxLength={44}
                                    value={project.workRequest ?? ''}
                                    onChange={this.onChange}
                                    formGroupClassName="mb-4"
                                />
                            )}

                            {contract?.companyRequiresPurchaseOrderNumber && (
                                <Input
                                    name="project.purchaseOrderNumber"
                                    label="PO Number"
                                    maxLength={44}
                                    value={project.purchaseOrderNumber ?? ''}
                                    onChange={this.onChange}
                                    formGroupClassName="mb-4"
                                />
                            )}

                            {(tenantSettings?.contractsCanEnforceProjectManagers && (contract ?? {}).requireProjectInformation) && (
                                <>
                                    <Input
                                        name="project.projectName"
                                        label="Project Name"
                                        value={project.projectName ?? ''}
                                        onChange={this.onChange}
                                        required
                                        error="Project Name is required."
                                        formGroupClassName="mb-4"
                                    />

                                    <ValidatedSelect
                                        id="projectManager"
                                        name="project.projectManagerId"
                                        label="Project Manager"
                                        options={contactsOptions?.projectManagerContactOptions || []}
                                        value={
                                            (contactsOptions?.projectManagerContactOptions || []).find(
                                                (x) => x.value === project?.projectManagerId,
                                            ) || ''}
                                        onChange={this.onSelectChanged}
                                        required
                                        error="A Project Manager selection is required."
                                        showAddButton
                                        onAddButtonClick={() => this.onAddContact('projectManagerId')}
                                        showEditButton={!!project?.projectManagerId}
                                        onEditButtonClick={() => this.onEditContact('projectManagerId')}
                                        showViewButton={!!project?.projectManagerId}
                                        onViewButtonClick={() => this.onEditContact('projectManagerId', true)}
                                    />
                                </>
                            )}

                            {contract?.invoiceHasInspectorName && (
                                <Input
                                    id="inspectorName"
                                    name="project.inspectorName"
                                    label="Inspector Name"
                                    value={project.inspectorName ?? ''}
                                    onChange={this.onChange}
                                    formGroupClassName="mb-4"
                                />
                            )}

                            {project?.jobTypeId !== 1 && (
                                <ValidatedSelect
                                    id="jobType"
                                    name="project.jobTypeId"
                                    formGroupClassName="mb-4"
                                    label="What type of work is being done?"
                                    options={jobTypesOptions}
                                    required={(jobTypesOptions ?? []).some((x) => x.timesheetRequiresWorkflowNotes)}
                                    error="A job type must be selected"
                                    value={
                                        (jobTypesOptions ?? []).find(
                                            (x) => x.value === project.jobTypeId,
                                        ) ?? ''
                                    }
                                    onChange={this.onSelectChanged}
                                />
                            )}
                            <Input
                                label="Billing Start Date"
                                id="billingStartDate"
                                required
                                type="date"
                                name="project.billingStartDate"
                                value={(project.billingStartDate ?? '').split('T')[0]}
                                onChange={this.onChange}
                                error="Billing Start Date is required."
                                disabled={!!project.lastInvoiced}
                            />
                            {!contract?.isQuickRentalContract && (
                                <LocationSelector
                                    label="Meeting Location"
                                    type="meetingLocation"
                                    stateData={project}
                                    contract={contract}
                                    usCounties={usCounties}
                                    addressFormRef={this.addressFormRef}
                                    setSelectedAddressType={(type) => this.setState({ selectedAddressType: type })}
                                    locationRequired={meetingLocationRequired}
                                />
                            )}

                            <LocationSelector
                                label="Job Location"
                                type="jobLocation"
                                stateData={project}
                                contract={contract}
                                usCounties={usCounties}
                                addressFormRef={this.addressFormRef}
                                setSelectedAddressType={(type) => this.setState({ selectedAddressType: type })}
                                locationRequired={jobLocationRequired}
                                allowSameAs={!contract?.isQuickRentalContract}
                            />
                        </Col>

                        <Col xs={6}>
                            <Textarea
                                name="project.notes"
                                label="Notes"
                                maxLength={1000}
                                value={project?.notes || ''}
                                rows={8}
                                onChange={this.onChange}
                            />
                        </Col>
                    </Row>

                    <div className="mt-5 mb-3">
                        <JobScheduler
                            onDateSelect={this.handleJobCreate}
                            project={{ ...project, contract: this.state.contract }}
                            dispatchLocations={dispatchLocations}
                            onJobSelect={this.handleJobSelect}
                            getJobById={this.getJobById}
                            setJobsSaving={this.setJobsSaving}
                            isSaving={saving || jobsSaving}
                            refreshProject={this.refreshProject}
                        />
                    </div>

                    {Boolean(contract?.isProjectContract) && (
                        <div className="mt-3 mb-3">
                            <UncontrolledCollapseUnderlineHeader
                                headerText="Equipment on Site"
                            >
                                <EquipmentOnSite resources={project.resources} />
                            </UncontrolledCollapseUnderlineHeader>
                        </div>
                    )}

                    {project?.id && <ProjectInvoices projectId={project.id} />}

                    <FlexCenterRow className="mt-4 mb-4">
                        <Button
                            type="submit"
                            color="primary"
                            name="projectForm"
                            disabled={saving}
                        >
                            {saving ? (
                                <div className="d-flex align-items-center justify-content-center">
                                    <Spinner
                                        size="sm"
                                        className="saving-button-progress text-success mr-2"
                                    />
                                    <span className="ml-2">Saving</span>
                                </div>
                            ) : (
                                <>
                                    <FontAwesomeIcon
                                        className="mr-1"
                                        icon={faSave}
                                    />
                                    {!existing ? 'Save New Project' : 'Save Project Details'}
                                </>
                            )}
                        </Button>

                        {project?.id && project?.jobs?.length > 0 && (
                            <Link
                                component={Button}
                                color="primary"
                                className="ml-2"
                                type="button"
                                to={dispatchPath}
                                outline
                            >
                                Go to Dispatch Board
                            </Link>
                        )}
                    </FlexCenterRow>

                    <FormBlocker
                        showProgress
                        show={saving || jobsSaving}
                    />
                </AppPageForm>

                {Boolean(selectedDate) && (
                    <JobForm
                        selectedDate={selectedDate}
                        contract={contract}
                        contractResources={contractResources}
                        project={project}
                        jobTypesOptions={jobTypesOptions}
                        resourceKits={resourceKits}
                        isRentalContract={Boolean(contract?.isProjectContract)}
                        slideOpen={slideOpen}
                        setSlideOpen={(open) => this.setState({ slideOpen: open })}
                        usCounties={usCounties}
                        contactsOptions={contactsOptions}
                        onUpdateContacts={this.onUpdateContacts}
                        dispatchLocations={dispatchLocations}
                        customerSubcontractors={customerSubcontractors}
                        contactFormRef={this.contactFormRef}
                        selectedJob={selectedJob}
                        onJobSave={this.handleJobSave}
                        refreshProject={this.refreshProject}
                        onDeleteJob={this.handleJobDelete}
                        onJobsBulkUpdate={this.handleRunApplyAllJobs}
                        viewOnly={hasOnlyJobViewAccess}
                    />
                )}

                {!loading && (
                    <>
                        {Boolean(usCounties) && (
                            <AddressFormNew
                                id="jobAddressForm"
                                ref={this.addressFormRef}
                                contractUsesPrevailingWage={contract?.prevailingWage || contract?.requirePaySchedule}
                                isUnionWork={contract?.isUnionWork}
                                contractId={contract?.id}
                                workTypeId={contract?.contractUnionWorkTypeId}
                                countryId={contract?.countryId}
                                countryCode={contract?.countryCode}
                                onSaveCallback={this.onSaveAddress}
                                usCounties={usCounties}
                                timeZone={selectedDispatchLocation?.timeZone}
                            />
                        )}

                        <OrganizationContactForm
                            ref={this.contactFormRef}
                            show={showContactsForm}
                            readOnly={contactFormIsReadOnly}
                            allowContactTypeEdit={allowContactTypeEdit}
                            contact={selectedContact}
                            onClose={() => this.setState({ showContactsForm: false, contactFormIsReadOnly: false })}
                            onChange={(e) => {
                                selectedContact[e.target.name] = e.target.value;
                                this.setState({
                                    selectedContact: { ...selectedContact },
                                });
                            }}
                            onCheckedChanged={(e) => {
                                selectedContact[e.target.name] = e.target.checked;
                                this.setState({
                                    selectedContact: { ...selectedContact },
                                });
                            }}
                            onContactTypeChanged={(items) => {
                                selectedContact.contactTypes = items.map(
                                    (x) => x.value,
                                );
                                this.setState({
                                    selectedContact: { ...selectedContact },
                                });
                            }}
                            onSaveCallback={this.onOrganizationContactSaved}
                        />
                    </>
                )}

                <EquipmentPickupModal
                    isOpen={showEquipmentPickupModal}
                    toggle={() => this.setState({ showEquipmentPickupModal: false })}
                    project={project}
                    contractId={project?.contractId}
                    onSuccess={async (timesheetNumber) => {
                        toasty.success(`Timesheet ${timesheetNumber} created successfully!`);
                        await this.refreshProject();
                        this.setState({ showEquipmentPickupModal: false });
                    }}
                />
            </>
        );
    }
}

const mapStateToProps = (state) => ({
    weekStartsOn: getWeekStartsOn(state),
});

const ConnectedProjectForm = connect(mapStateToProps)(ProjectForm);

const ProjectFormWithQueryClient = (props) => {
    const queryClient = useQueryClient();

    return (
        <ConnectedProjectForm
            {...props}
            queryClient={queryClient}
        />
    );
};

export default ProjectFormWithQueryClient;
